/**
 * The unit model, browser side.
 *
 * The canonical implementation lives in the edge tree —
 * `supabase/functions/_shared/packageLadder.mjs`, `countEntryMath.mjs` and
 * `parAndMoq.mjs` — because it depends on the Recipe Lab unit engine
 * (`menu-recipe-costing/units.mjs`), a large conversion table that already has
 * one hand-maintained browser copy (`public/js/menu-costing-units.js`). A
 * second copy would be a third place for the factors to drift, and drift in a
 * conversion table is silent: nothing throws, the numbers are just wrong.
 *
 * So this module does no unit conversion at all. The server decomposes each
 * vendor package into a LADDER of countable rungs — case → 144 fl oz, bottle →
 * 6 fl oz, fl oz → 1 — and ships the rungs with the session. Each rung already
 * carries `qtyInCountUnit`, so the browser only ever multiplies and adds. That
 * keeps the arithmetic here trivial enough to be obviously correct, keeps
 * counting working offline (the rungs travel with the cached session), and
 * leaves exactly one place where a conversion factor is written down.
 *
 * What IS mirrored here is the set of DECISIONS, because they have to hold on
 * both sides of the wire:
 *
 *   - An entry whose rung is missing is unresolved. It is excluded from the
 *     total and reported. It is never added as zero, because "we could not
 *     work it out" and "there is none on the shelf" order very different
 *     amounts of stock.
 *   - A case-counted item may hold 0.5 case. When the item instead counts in
 *     bottles, half of its 24-bottle case is entered as 12 bottles so the
 *     conversion stays explicit.
 *   - A missing number renders as "—", never as 0 or $0.00.
 *
 * `countUnitModel.test.js` imports the edge modules alongside this one and
 * asserts both produce the same totals for the same worked examples, so the
 * two implementations cannot drift apart unnoticed.
 */

// The one shared import: vendor retirement is a single implementation across
// both runtimes (see vendorState.mjs) — it is pure selection, not conversion.
import { vendorIsRetired } from '../../../supabase/functions/_shared/vendorState.mjs';

/** Rung levels, coarsest first. Mirrors packageLadder.LADDER_LEVELS. */
export const LADDER_LEVELS = Object.freeze({
  PACKAGE: 'package',
  INNER_PACK: 'inner_pack',
  CANONICAL: 'canonical',
});

/** Mirrors packageLadder.ENTRY_KINDS and the entry_kind CHECK constraint. */
export const ENTRY_KINDS = Object.freeze([
  'canonical_measure',
  'full_package',
  'loose_inner_pack',
  'partial_package',
  'manual_measure',
]);

export const UNRESOLVED_REASONS = Object.freeze({
  empty_entry: 'This entry is empty.',
  bad_quantity: 'The quantity must be a number of zero or more.',
  unknown_count_unit: "The item's count unit is not one we can convert.",
  unresolved_package: 'This vendor package has no confirmed size, so it cannot be added to the total.',
  unknown_entered_unit: 'That unit is not one we can convert.',
  incompatible_units: "That unit cannot be converted into the item's count unit.",
});

const round6 = (n) => Math.round(n * 1e6) / 1e6;

// Number(null), Number('') and Number([]) are all 0. A missing quantity has to
// be rejected before coercion or it arrives as a legitimate count of zero.
export const finiteNonNegative = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const PACKAGE_ENTRY_KINDS = new Set(['full_package', 'loose_inner_pack', 'partial_package']);

const findRung = (rungs, entry) => {
  if (!entry) return null;
  const optionId = entry.purchase_option_id ?? null;
  if (optionId === null) {
    // Mirror of countEntryMath: a package-kind entry with no option row (the
    // option was deleted) has lost its definition — it must not fall through
    // to the canonical rung and re-read "2 cases" as "2 fl oz".
    if (PACKAGE_ENTRY_KINDS.has(entry.entry_kind)) return null;
    return (rungs || []).find((r) => r.level === LADDER_LEVELS.CANONICAL) || null;
  }
  const wanted =
    entry.entry_kind === 'loose_inner_pack' ? LADDER_LEVELS.INNER_PACK : LADDER_LEVELS.PACKAGE;
  return (
    (rungs || []).find(
      (r) => r.optionId != null && String(r.optionId) === String(optionId) && r.level === wanted,
    ) || null
  );
};

/**
 * Resolves one entry against the server-supplied ladder.
 *
 * A plain measure typed in a unit other than the item's count unit cannot be
 * resolved here — that needs the conversion table — so it is returned
 * unresolved with `needsServer: true` and the caller asks the server. The
 * common case, a quantity in the item's own unit or in a vendor package, is
 * pure multiplication and stays local.
 *
 * @returns {{ status:'valid'|'unresolved', normalized:number|null,
 *             reason:string|null, reasonText:string|null,
 *             rung:object|null, needsServer:boolean, warnings:string[] }}
 */
export function resolveEntry(entry, rungs) {
  const warnings = [];
  const unresolved = (reason, needsServer = false) => ({
    status: 'unresolved',
    normalized: null,
    reason,
    reasonText: UNRESOLVED_REASONS[reason] || 'This entry could not be converted.',
    rung: null,
    needsServer,
    warnings,
  });

  if (!entry) return unresolved('empty_entry');

  const quantity = finiteNonNegative(entry.entered_quantity);
  if (quantity === null) return unresolved('bad_quantity');

  // A quantity the server already normalized (loaded from the database, or
  // returned by a preview call) is authoritative — never recompute it.
  const stored = finiteNonNegative(entry.normalized_quantity);
  if (entry.conversion_status === 'valid' && stored !== null) {
    return {
      status: 'valid',
      normalized: stored,
      reason: null,
      reasonText: null,
      rung: findRung(rungs, entry),
      needsServer: false,
      warnings,
    };
  }
  if (entry.conversion_status === 'unresolved') {
    // The default reason depends on what the entry IS: a vendor package with no
    // confirmed size, or a measure in a unit we have no factor for. Reporting
    // the wrong one sends the user to fix the wrong thing.
    const fallback = entry.purchase_option_id != null ? 'unresolved_package' : 'unknown_entered_unit';
    return unresolved(entry.conversion_reason || fallback);
  }

  const rung = findRung(rungs, entry);
  if (!rung) {
    return unresolved(entry.purchase_option_id != null ? 'unresolved_package' : 'unknown_count_unit');
  }

  // A measure typed in some other unit needs the conversion table.
  if (rung.level === LADDER_LEVELS.CANONICAL && entry.entered_unit && entry.entered_unit !== rung.unitLabel) {
    return unresolved('unknown_entered_unit', true);
  }

  let usable = quantity;
  if (!rung.allowsFractional && !Number.isInteger(quantity)) {
    usable = Math.floor(quantity);
    warnings.push(
      `${rung.label} is counted in whole ${rung.unitLabel}s; ${quantity} was rounded down to ${usable}.`,
    );
  }

  return {
    status: 'valid',
    normalized: round6(usable * rung.qtyInCountUnit),
    reason: null,
    reasonText: null,
    rung,
    needsServer: false,
    warnings,
  };
}

/**
 * The on-hand total for one count line, from its raw entries.
 *
 * @returns {{ total:number|null, resolved:Array, unresolved:Array,
 *             hasUnresolved:boolean, needsServer:boolean, warnings:string[] }}
 *   `total` is null — never 0 — when there are entries but none resolved.
 */
export function totalFromEntries(entries, rungs) {
  const resolved = [];
  const unresolved = [];
  const warnings = [];
  let needsServer = false;

  (entries || []).forEach((entry, index) => {
    const result = resolveEntry(entry, rungs);
    warnings.push(...result.warnings);
    if (result.needsServer) needsServer = true;
    const base = { ...entry, sort_order: entry?.sort_order ?? index };
    if (result.status === 'valid') {
      resolved.push({ ...base, normalized_quantity: result.normalized, rung: result.rung });
    } else {
      unresolved.push({
        ...base,
        normalized_quantity: null,
        reason: result.reason,
        reasonText: result.reasonText,
      });
    }
  });

  const total =
    resolved.length > 0
      ? round6(resolved.reduce((sum, e) => sum + e.normalized_quantity, 0))
      : null;

  if (unresolved.length > 0) {
    const many = unresolved.length !== 1;
    warnings.push(
      `${unresolved.length} ${many ? 'entries are' : 'entry is'} not included in the total because ` +
        `${many ? 'their' : 'its'} package size is unconfirmed.`,
    );
  }

  return {
    total,
    resolved,
    unresolved,
    hasUnresolved: unresolved.length > 0,
    needsServer,
    warnings,
  };
}

/**
 * "1 tub + 2 cases + 8 bottles + 2.5 gal" — how the total was arrived at,
 * shown beside it so the number is auditable without opening the entries.
 */
export function describeEntries(resolvedEntries) {
  return (resolvedEntries || [])
    .map((entry) => {
      const qty = Number(entry?.entered_quantity);
      if (!Number.isFinite(qty)) return null;
      const unit = entry.rung?.unitLabel || entry.entered_unit || '';
      // Package nouns pluralize ("2 cases"); measure units do not ("2.5 gal").
      const isPackageNoun =
        entry.rung?.level === LADDER_LEVELS.PACKAGE || entry.rung?.level === LADDER_LEVELS.INNER_PACK;
      const label = isPackageNoun && qty !== 1 && unit && !unit.endsWith('s') ? `${unit}s` : unit;
      return `${round6(qty)} ${label}`.trim();
    })
    .filter(Boolean)
    .join(' + ');
}

/* ── Display ─────────────────────────────────────────────────────────────── */

/** The placeholder for a number we do not have. Never "0", never "$0.00". */
export const MISSING = '—';

/**
 * A quantity with its unit, e.g. "10.125 gal". Returns MISSING for null so a
 * missing count can never read as an empty shelf.
 */
export function formatQuantity(value, unit, { maxDecimals = 3 } = {}) {
  const n = finiteNonNegative(value);
  if (n === null) return MISSING;
  const rounded = Math.round(n * 10 ** maxDecimals) / 10 ** maxDecimals;
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  return unit ? `${text} ${unit}` : text;
}

/** "3 cases" / "1 tub" — a package count with the vendor's own noun. */
export function formatPackages(count, packageNoun) {
  const n = finiteNonNegative(count);
  if (n === null) return MISSING;
  const noun = packageNoun || 'package';
  const plural = n === 1 || noun.endsWith('s') ? noun : `${noun}s`;
  return `${n} ${plural}`;
}

/* ── PAR and ordering ────────────────────────────────────────────────────── */

export const PAR_STATUS = Object.freeze({
  VALID: 'valid',
  REQUIRES_OPTION: 'requires_option',
  UNRESOLVED: 'unresolved',
  EMPTY: 'empty',
});

// The factor-1 count-unit synonyms, mirroring the server's units.mjs
// COUNT_UNITS: 'ea', 'each', 'ct' and 'piece' are the same measurement. The
// legacy-PAR gate below compares through this set so a PAR stated as 'each'
// on a row whose snapshot says 'ea' resolves instead of reading as a unit
// mismatch — the server engine (normalizeUnit) already treats them equal, and
// the mirror must agree. A lookup, not a conversion engine.
const COUNT_UNIT_SYNONYMS = new Set([
  'each', 'ea', 'count', 'ct', 'cnt', 'piece', 'pieces', 'pc', 'pcs', 'unit', 'units',
]);

const sameCountUnit = (a, b) => {
  const left = String(a || '').toLowerCase().trim();
  const right = String(b || '').toLowerCase().trim();
  if (!left || !right) return false;
  if (left === right) return true;
  return COUNT_UNIT_SYNONYMS.has(left) && COUNT_UNIT_SYNONYMS.has(right);
};

/**
 * The PAR to compare on-hand against, for a count row that may predate
 * normalization.
 *
 * Precedence mirrors `parAndMoq.resolveRowPar`: a stored normalized value wins,
 * because it was computed when the package was confirmed and must not drift;
 * otherwise the legacy par_level is used only when par_unit already IS the
 * count unit. A legacy PAR in some other unit is reported unresolved rather
 * than subtracted as though the units matched — "2" cases against "10" gallons
 * is not a shortage of 8 anything.
 */
export function resolveRowPar(row, countUnit) {
  if (!row) return { status: PAR_STATUS.EMPTY, normalized: null, display: null, unit: countUnit || null };

  const stored = finiteNonNegative(row.par_normalized_quantity);
  if (stored !== null) {
    return {
      status: PAR_STATUS.VALID,
      normalized: stored,
      unit: row.par_normalized_unit || countUnit || null,
      display: {
        value: finiteNonNegative(row.par_display_quantity) ?? finiteNonNegative(row.par_level),
        unit: row.par_display_unit || row.par_unit || countUnit || null,
        optionId: row.par_purchase_option_id ?? null,
      },
    };
  }

  // expected_quantity is the pre-par_level PAR column; rows old enough to
  // carry only it still state a PAR, under the same unit gate below.
  const legacy = finiteNonNegative(row.par_level) ?? finiteNonNegative(row.expected_quantity);
  if (legacy === null) {
    return { status: PAR_STATUS.EMPTY, normalized: null, display: null, unit: countUnit || null };
  }

  const parUnit = (row.par_unit || '').trim();
  // A missing ITEM unit is not a match — the server refuses this case as
  // unknown_count_unit and the mirror must too, or '2 cases' PAR on a
  // unit-less item silently counts as 2 of nothing in the browser only.
  // Count synonyms ('ea' vs 'each') match through the shared set above.
  const same = !parUnit || sameCountUnit(parUnit, countUnit);
  if (same) {
    return {
      status: PAR_STATUS.VALID,
      normalized: legacy,
      unit: countUnit || parUnit || null,
      display: { value: legacy, unit: parUnit || countUnit || null, optionId: null },
    };
  }

  return {
    status: PAR_STATUS.UNRESOLVED,
    normalized: null,
    unit: countUnit || null,
    display: { value: legacy, unit: parUnit, optionId: null },
  };
}

/**
 * The par_normalized_* refresh that must travel with EVERY count-page PAR
 * write. Leaving the stored normalized PAR stale while the raw pair moves is
 * the "two PAR truths on one row" failure: normalized-first readers keep the
 * old target while the UI shows the new one. A value typed in the row's own
 * count unit (or with no unit) refreshes the quintet; a value typed in some
 * other unit CLEARS it, so the legacy pair reads as unresolved — the truth.
 *
 * Shared by CountInterface (PAR edits + pars wizard), CountSessionItemEditor
 * and the TaskAlertDashboard wizard, so no writer can drift.
 */
export function buildParNormalizationPatch({ parLevel, parUnit, countUnit } = {}) {
  const typedUnit = String(parUnit || '').trim();
  const unit = countUnit == null ? '' : String(countUnit).trim();
  const value = finiteNonNegative(parLevel);
  const same = !typedUnit || sameCountUnit(typedUnit, unit);
  if (same && value !== null) {
    return {
      par_normalized_quantity: value,
      par_normalized_unit: unit || typedUnit || null,
      par_display_quantity: value,
      par_display_unit: unit || typedUnit || null,
      par_purchase_option_id: null,
    };
  }
  return {
    par_normalized_quantity: null,
    par_normalized_unit: null,
    par_display_quantity: value,
    par_display_unit: typedUnit || null,
    par_purchase_option_id: null,
  };
}

/**
 * The vendor-neutral PAR, as columns. count_items has carried these since
 * migration 20260730120000 and count_template_items since 20260822120000.
 *
 * They travel TOGETHER or not at all. A row holding a fresh par_level beside
 * a stale par_normalized_quantity has two PARs on it, and the normalized-first
 * readers (resolveRowPar everywhere) will keep answering with the old one.
 */
export const PAR_NORMALIZATION_COLUMNS = Object.freeze([
  'par_normalized_quantity',
  'par_normalized_unit',
  'par_display_quantity',
  'par_display_unit',
  'par_purchase_option_id',
]);

/** Comma-joined for a PostgREST `.select()` list. */
export const PAR_NORMALIZATION_SELECT = PAR_NORMALIZATION_COLUMNS.join(', ');

/**
 * The normalization quintet lifted off a row, for copying a PAR from one
 * table to another (template → session seed, session → session seed).
 * Absent columns come back as explicit nulls so a partial copy cannot leave a
 * stale value behind on the destination row.
 */
export function parNormalizationFrom(row) {
  const patch = {};
  PAR_NORMALIZATION_COLUMNS.forEach((column) => {
    patch[column] = row?.[column] ?? null;
  });
  return patch;
}

/**
 * A store behind on migration 20260730120000 (count_items) or 20260822120000
 * (count_template_items) has none of the normalization columns; readers and
 * writers retry with the legacy pair alone. One matcher, so every PAR path
 * degrades identically.
 */
export function isMissingParNormalizationColumnError(error) {
  if (!error) return false;
  return error.code === '42703' || error.code === 'PGRST204'
    || /par_normalized|par_display|par_purchase_option/.test(String(error.message || ''));
}

/**
 * NEED = max(0, PAR − on hand), in the item's count unit.
 *
 * Returns null when either side is unknown. An unresolved PAR must not become
 * a need of zero (silently stops ordering an item that has a PAR), and an
 * unresolved on-hand must not become a need of the whole PAR (orders a full
 * shelf of stock that may already be there). Both cases are the caller's to
 * disclose, which it cannot do if this function hands back a number.
 */
export function computeNeed({ par, onHand } = {}) {
  const parQty = finiteNonNegative(par);
  if (parQty === null) return { need: null, reason: 'no_par' };
  const hand = finiteNonNegative(onHand);
  if (hand === null) return { need: null, reason: 'not_counted' };
  return { need: round6(Math.max(0, parQty - hand)), reason: null };
}

/**
 * Whole packages to order for a need, and what actually arrives.
 *
 *   packages = max(MOQ, ceil(need / qtyPerPackage))
 *
 * The ceiling is what makes ordering honest: you cannot buy 1.4 cases, so you
 * buy 2 and carry the overage. `allowsFractional` is the narrow exception —
 * vendors who cut to order — and defaults off so no option silently starts
 * accepting half-cases.
 *
 * @returns {{ packages:number|null, quantityReceived:number|null,
 *             overage:number|null, reason:string|null }}
 */
export function packagesForNeed({ need, qtyPerPackage, moqPackages, allowsFractional = false, orderIncrement } = {}) {
  const needQty = finiteNonNegative(need);
  if (needQty === null) return { packages: null, quantityReceived: null, overage: null, reason: 'unknown_need' };

  // The legal ordering step, in packages. Unstated means whole packages —
  // except for a cut-to-order vendor, where it means the vendor cuts whatever
  // is asked. A STATED fractional step (0.25 on a by-the-pound option) steps
  // even a cut-to-order order: 6.1 lb becomes 6.25 lb, never 6.1.
  const incRaw = Number(orderIncrement);
  const increment = Number.isFinite(incRaw) && incRaw > 0 ? incRaw : allowsFractional ? null : 1;
  // 1e-9 absorbs float noise before the ceiling: 3/1.5 must be 2 steps, not 3.
  const snapUp = (value) => round6(Math.ceil(value / increment - 1e-9) * increment);

  const moqRaw = Number(moqPackages);
  const stated = Number.isFinite(moqRaw) && moqRaw > 0 ? moqRaw : null;
  // With no stated minimum, a whole-package vendor still has one implicitly —
  // you cannot buy less than one case. A cut-to-order vendor does not, and
  // clamping it to 1 would turn a 0.7 lb order into a full package.
  const moq = increment === null
    ? (stated !== null ? Math.ceil(stated) : 0)
    : stated !== null
      ? snapUp(stated)
      : allowsFractional
        ? 0
        : snapUp(1);

  if (needQty <= 0) return { packages: 0, quantityReceived: 0, overage: 0, reason: null };

  const perRaw = Number(qtyPerPackage);
  const per = Number.isFinite(perRaw) && perRaw > 0 ? perRaw : null;
  if (per === null) {
    // Without a package size we cannot say how many packages cover the need.
    // The MOQ is the only defensible number — at least one, since an unknown
    // package cannot be subdivided — and the overage is unknown.
    return { packages: Math.max(1, moq), quantityReceived: null, overage: null, reason: 'unresolved_package' };
  }

  const exact = needQty / per;
  const packages = increment === null
    ? Math.max(moq, round6(exact))
    : Math.max(moq, snapUp(exact));
  const quantityReceived = round6(packages * per);
  return { packages, quantityReceived, overage: round6(quantityReceived - needQty), reason: null };
}

/* ── MOQ ─────────────────────────────────────────────────────────────────── */

export const MOQ_SOURCES = Object.freeze({
  SELECTED_OPTION: 'selected_option',
  PREFERRED_OPTION: 'preferred_option',
  ONLY_OPTION: 'only_option',
  LEGACY_ROW: 'legacy_row',
  DEFAULT: 'default',
});

const positiveInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.ceil(n) : null;
};

/**
 * The minimum packages for a line, and where the number came from. Mirrors
 * `parAndMoq.resolveMoqPackages`.
 *
 * A single item-level MOQ cannot be right for an item bought four ways, so the
 * legacy column is a last resort and is never applied on top of a chosen
 * vendor: "2" was entered when the item had one supplier, and applying it to
 * somebody else's case is how you order two pallets by accident.
 */
export function resolveMoqPackages({ selectedOption, options, legacyMoq } = {}) {
  // A retired vendor's MOQ must never enter a new-order calculation — its
  // package may still describe stock on the shelf, but its minimum is not a
  // constraint on anyone anymore.
  const active = (options || []).filter((o) => o && o.status === 'active' && !vendorIsRetired(o));
  const distinct = new Set(active.map((o) => positiveInt(o.moq_packages)).filter((m) => m !== null));
  const variesByVendor = active.length >= 2 && distinct.size >= 2;

  const from = (option, source) => {
    if (vendorIsRetired(option)) return null;
    const moq = positiveInt(option?.moq_packages);
    return moq === null ? null : { packages: moq, source, optionId: option.id ?? null, variesByVendor };
  };

  const selected = from(selectedOption, MOQ_SOURCES.SELECTED_OPTION);
  if (selected) return selected;

  if (!selectedOption) {
    const preferred = from(active.find((o) => o.is_preferred === true), MOQ_SOURCES.PREFERRED_OPTION);
    if (preferred) return preferred;
    if (active.length === 1) {
      const only = from(active[0], MOQ_SOURCES.ONLY_OPTION);
      if (only) return only;
    }
    const legacy = positiveInt(legacyMoq);
    if (legacy !== null) {
      return { packages: legacy, source: MOQ_SOURCES.LEGACY_ROW, optionId: null, variesByVendor };
    }
  }

  return { packages: 1, source: MOQ_SOURCES.DEFAULT, optionId: selectedOption?.id ?? null, variesByVendor };
}

/** "2 cases minimum", or "MOQ varies by vendor" when nothing is chosen yet. */
export function describeMoq(resolution, option) {
  if (!resolution) return null;
  if (resolution.source === MOQ_SOURCES.DEFAULT && resolution.variesByVendor) {
    return 'MOQ varies by vendor';
  }
  const unit = (option?.purchase_unit || '').trim() || 'package';
  const plural = resolution.packages === 1 || unit.endsWith('s') ? unit : `${unit}s`;
  return `${resolution.packages} ${plural} minimum`;
}
